import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, onMount, Show } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { useSession } from "@/lib/auth";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import {
  cn,
  type ListGroup,
  type ListWithMeta,
  mapSmartViewCounts,
  queryKeys,
  type SmartViewCounts,
} from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useListTodoCounts } from "@/features/todos/hooks/use-list-todo-counts";

import { DashboardSmartViews } from "./dashboard-smart-views";
import { DashboardLists } from "./dashboard-lists";
import { OnboardingCard } from "@/features/onboarding/components/onboarding-card";

interface DashboardProps {
  onAddList: (groupId?: string) => void;
  /** Initial search for lists/folders read from URL (`?q=`). */
  searchQuery?: string;
  /** Persists search (debounced) to URL: shareable filtered view. */
  onSearchQueryChange?: (query: string) => void;
  /** Collapsed groups read from URL (`?collapsed=`). */
  collapsedGroups?: string[];
  /** Persists collapsed groups to URL: shareable dashboard state. */
  onCollapsedGroupsChange?: (ids: string[]) => void;
}

let lastUserId: string | undefined = undefined;

export function Dashboard(props: DashboardProps) {
  const { t } = useI18n();
  const session = useSession();
  const { userId } = session;
  const queryClient = useQueryClient();

  // --- Synchronous Cache Loading Helpers ---
  const getInitialCachedCounts = () => {
    const uid = userId();
    if (!uid) {
      return undefined;
    }
    const cached = queryClient.getQueryData<SmartViewCounts | SmartViewCounts[]>(
      queryKeys.smartViews.counts(uid),
    );
    return Array.isArray(cached) ? cached[0] : cached;
  };
  const getInitialCachedLists = () => {
    const uid = userId();
    return uid
      ? (queryClient.getQueryData<ListWithMeta[]>(queryKeys.lists.customLists(uid)) ?? [])
      : [];
  };
  const getInitialCachedGroups = () => {
    const uid = userId();
    return uid ? (queryClient.getQueryData<ListGroup[]>(queryKeys.lists.groups(uid)) ?? []) : [];
  };

  // --- State Declarations ---
  const [animationActive, setAnimationActive] = createSignal(true);
  const [listsStore, setListsStore] = createStore<{ data: ListWithMeta[] }>({
    data: getInitialCachedLists(),
  });
  const [groupsStore, setGroupsStore] = createStore<{ data: ListGroup[] }>({
    data: getInitialCachedGroups(),
  });
  const [listsLoadedOnce, setListsLoadedOnce] = createSignal(
    (() => {
      const uid = userId();
      if (!uid) {
        return false;
      }
      return (
        queryClient.getQueryData(queryKeys.lists.customLists(uid)) !== undefined &&
        queryClient.getQueryData(queryKeys.lists.groups(uid)) !== undefined
      );
    })(),
  );
  const [stableCounts, setStableCounts] = createSignal<SmartViewCounts | undefined>(
    getInitialCachedCounts(),
  );

  // Reset all stores and loaded flags when active user ID changes to prevent cross-account flickering or data leakage
  createEffect(() => {
    const currentUid = userId();
    if (currentUid !== lastUserId) {
      setListsStore("data", []);
      setGroupsStore("data", []);
      setStableCounts(undefined);
      setListsLoadedOnce(false);
      lastUserId = currentUid;
    }
  });

  onMount(() => {
    // Disable the animation class after it completes to prevent it from replaying on theme recalculations
    const ANIMATION_DURATION_MS = 600;
    setTimeout(() => {
      setAnimationActive(false);
    }, ANIMATION_DURATION_MS);
  });

  // These queries are already managed (subscribed + fetched) by app-layout-content.tsx
  // and route.tsx. We only read from the shared TanStack cache here — no new
  // PowerSync subscriptions, no duplicate fetches.
  // Read-only cache mirrors: these never fetch (`enabled: false`, no queryFn).
  // The layout's non-suspending PowerSync subscriptions own the fetch and push
  // rows into the TanStack cache via setQueryData; these observers just reflect
  // those updates reactively into the stores below. Keeping them disabled
  // avoids the "Missing queryFn" throw (and the Suspense flash it caused) on a
  // cold cache.
  const countsQuery = createQuery<SmartViewCounts | SmartViewCounts[]>(() => ({
    queryKey: queryKeys.smartViews.counts(userId()),
    enabled: false,
    // NO `keepPreviousData`: these keys are scoped to the ACTIVE ACCOUNT, so
    // "previous data" after a key change belongs to ANOTHER ACCOUNT. That
    // is what caused the dashboard to render the account you just left
    // ("delayed load of previous account"). A brief skeleton is the
    // correct response while rows for the new account arrive.
    staleTime: Infinity,
  }));

  const lists = createQuery<ListWithMeta[]>(() => ({
    queryKey: queryKeys.lists.customLists(userId()),
    enabled: false,
    // NO `keepPreviousData`: these keys are scoped to the ACTIVE ACCOUNT, so
    // "previous data" after a key change belongs to ANOTHER ACCOUNT. That
    // is what caused the dashboard to render the account you just left
    // ("delayed load of previous account"). A brief skeleton is the
    // correct response while rows for the new account arrive.
    staleTime: Infinity,
  }));

  const listGroups = createQuery<ListGroup[]>(() => ({
    queryKey: queryKeys.lists.groups(userId()),
    enabled: false,
    // NO `keepPreviousData`: these keys are scoped to the ACTIVE ACCOUNT, so
    // "previous data" after a key change belongs to ANOTHER ACCOUNT. That
    // is what caused the dashboard to render the account you just left
    // ("delayed load of previous account"). A brief skeleton is the
    // correct response while rows for the new account arrive.
    staleTime: Infinity,
  }));

  // (listsStore and groupsStore moved to top of component body)

  // Cache population is handled synchronously in the state initialization above.

  // 2. Reconcile live query data
  createEffect(() => {
    const rawLists = lists.data;
    if (rawLists !== undefined) {
      setListsStore("data", reconcile(rawLists, { key: "id" }));
    }
  });

  createEffect(() => {
    const rawGroups = listGroups.data;
    if (rawGroups !== undefined) {
      setGroupsStore("data", reconcile(rawGroups, { key: "id" }));
    }
  });

  // 3. Mark lists as loaded only when both live queries have completed
  createEffect(() => {
    if (lists.data !== undefined && listGroups.data !== undefined) {
      setListsLoadedOnce(true);
    }
  });

  const todoCounts = useListTodoCounts({ userId });

  // 4. Update stable counts from live data
  createEffect(() => {
    const uid = userId();
    if (!uid) {
      return;
    }

    const rawCounts = countsQuery.data ?? null;
    if (rawCounts !== null) {
      const singleCounts = Array.isArray(rawCounts) ? rawCounts[0] : rawCounts;
      setStableCounts(singleCounts);
    }
  });

  const stats = createMemo(() => {
    const s = stableCounts();
    return s ? mapSmartViewCounts(s) : undefined;
  });
  const firstName = (): string | undefined => session.data?.user.name?.split(" ")[0];

  return (
    <div
      class={cn(
        "flex-1 min-h-0 overflow-y-auto p-6 md:p-10 [scrollbar-gutter:stable]",
        animationActive() && "animate-fade-in",
      )}
      style={{
        "overflow-anchor": "none",
        "will-change": "scroll-position",
      }}
    >
      <div class="mx-auto max-w-7xl space-y-10">
        <header class="space-y-4">
          <h1 class="text-6xl font-extrabold tracking-tighter">Listto</h1>
          <Show
            when={session.isAuthenticated()}
            fallback={
              <p class="text-base text-muted-foreground max-w-md">
                {t(
                  msg`You are in visitor mode. Sign in to sync your tasks and access all features.`,
                )}
              </p>
            }
          >
            <div class="flex flex-col">
              <span class="text-lg font-medium text-foreground/80">
                {(() => {
                  const name = firstName() ?? t(msg`User`);
                  return t(msg`Hello, ${name}`);
                })()}
              </span>
              <span class="text-sm text-muted-foreground">{session.data?.user.email}</span>
            </div>
          </Show>
        </header>

        {/* K2 — onboarding: oferta de seed de ejemplo SOLO para invitado prístino. */}
        <OnboardingCard />

        <section class="space-y-2">
          <DashboardSmartViews stats={stats()} />
        </section>

        <section>
          <DashboardLists
            lists={listsStore.data}
            groups={groupsStore.data}
            todoCounts={todoCounts.data()}
            onAddList={props.onAddList}
            isLoading={!listsLoadedOnce() && listsStore.data.length === 0}
            isCountsLoading={todoCounts.isLoading()}
            initialQuery={props.searchQuery}
            onQueryChange={props.onSearchQueryChange}
            collapsedGroups={props.collapsedGroups}
            onCollapsedGroupsChange={props.onCollapsedGroupsChange}
          />
        </section>
      </div>
    </div>
  );
}
